import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { TextField, Section, PageHeader } from "./AdminUI";
import ImageUploader from "./ImageUploader";

const TestimonialsAdmin = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await api.listTestimonials());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateLocal = (id, patch) =>
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const onSave = async (item) => {
    setSavingId(item.id);
    try {
      const saved = await api.updateTestimonial(item.id, item);
      updateLocal(item.id, saved);
      toast({ title: "Saved", description: `"${item.name}" updated.` });
    } catch (e) {
      toast({ title: "Couldn't save", description: e.message });
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.deleteTestimonial(id);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast({ title: "Deleted" });
    } catch (e) {
      toast({ title: "Couldn't delete", description: e.message });
    }
  };

  const onAdd = async () => {
    try {
      const created = await api.createTestimonial({
        name: "New testimonial",
        role: "",
        quote: "",
        avatar: "",
        order: items.length + 1,
      });
      setItems((arr) => [...arr, created]);
    } catch (e) {
      toast({ title: "Couldn't add", description: e.message });
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline-block text-[color:var(--muted)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        label="Testimonials"
        title="Kind words from clients"
        action={
          <button onClick={onAdd} className="mk-btn mk-btn-primary">
            <Plus className="w-4 h-4" /> Add testimonial
          </button>
        }
      />

      {items.length === 0 && (
        <div className="py-16 text-center text-[color:var(--muted)] border border-dashed border-black/15 rounded-2xl">
          No testimonials yet.
        </div>
      )}

      {items.map((item) => (
        <Section key={item.id} title={item.name || "Untitled testimonial"} hint={`Order ${item.order ?? "—"}`}>
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <ImageUploader
                label="Photo (optional)"
                aspect="aspect-[4/5]"
                value={item.avatar}
                onChange={(v) => updateLocal(item.id, { avatar: v })}
              />
            </div>
            <div className="md:col-span-8 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Name" value={item.name} onChange={(v) => updateLocal(item.id, { name: v })} />
                <TextField label="Role / company" value={item.role} onChange={(v) => updateLocal(item.id, { role: v })} />
              </div>
              <TextField label="Quote" textarea rows={4} value={item.quote} onChange={(v) => updateLocal(item.id, { quote: v })} />
              <TextField label="Order" type="number" value={item.order} onChange={(v) => updateLocal(item.id, { order: Number(v) || 0 })} />
            </div>
          </div>
          <div className="flex justify-between pt-2">
            <button type="button" onClick={() => onDelete(item.id)} className="inline-flex items-center gap-2 text-sm text-[color:var(--coral)] hover:underline">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button type="button" onClick={() => onSave(item)} disabled={savingId === item.id} className="mk-btn mk-btn-primary disabled:opacity-60">
              <Save className="w-4 h-4" /> {savingId === item.id ? "Saving…" : "Save"}
            </button>
          </div>
        </Section>
      ))}
    </div>
  );
};

export default TestimonialsAdmin;
